'use client';

import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/context/AuthContext';
import { apiFetch } from '@/lib/api/client';
import { formatLessonDate } from '@/lib/booking/format';
import { getCancellationPolicySummary } from '@/lib/booking/policy';
import type { BookableSlot, PackageProduct } from '@/lib/booking/types';

type BookingRecord = {
  id: string;
  slot_id: string;
  starts_at: string;
  status: string;
  payment_status: string;
  lesson_type_id: string;
  lesson_types?: { name: string; duration_minutes: number } | null;
  locations?: { name: string } | null;
};

type BalanceRecord = {
  id: string;
  balance: number;
  lesson_types?: { name: string; duration_minutes: number; code: string } | null;
};

type AccountResponse = {
  profile: {
    full_name: string | null;
    role: 'user' | 'admin';
  } | null;
  upcomingBookings: BookingRecord[];
  pastBookings: BookingRecord[];
  balances: BalanceRecord[];
};

export default function AccountPageClient() {
  const { session, user, profile, loading: authLoading, signOut } = useAuth();
  const router = useRouter();
  const [loading, setLoading] = useState(true);
  const [submittingBookingId, setSubmittingBookingId] = useState<string | null>(null);
  const [rescheduleBookingId, setRescheduleBookingId] = useState<string | null>(null);
  const [rescheduleSlotId, setRescheduleSlotId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AccountResponse | null>(null);
  const [packages, setPackages] = useState<PackageProduct[]>([]);
  const [openSlots, setOpenSlots] = useState<BookableSlot[]>([]);

  const loadOverview = async () => {
    setLoading(true);
    try {
      const response = await apiFetch<AccountResponse>('/api/account/overview', {
        method: 'GET',
        session,
      });
      setData(response);
      setError(null);
      const packageResponse = await apiFetch<{ packages: PackageProduct[] }>('/api/packages', {
        method: 'GET',
        session,
      });
      const bootstrapResponse = await apiFetch<{ slots: BookableSlot[] }>('/api/book/bootstrap', {
        method: 'GET',
        session,
      });
      setPackages(packageResponse.packages.filter((entry) => entry.account_required));
      setOpenSlots(bootstrapResponse.slots);
    } catch (overviewError) {
      const message =
        overviewError instanceof Error ? overviewError.message : 'Failed to load account';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && user && profile?.role === 'admin') {
      router.replace('/admin');
      return;
    }
    if (!authLoading && user) {
      loadOverview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, profile?.role, session?.access_token, user?.id]);

  const canShowGate = !authLoading && !user;

  const handleCancel = async (bookingId: string) => {
    const confirmed = window.confirm(
      'Are you sure you want to cancel this booking? Cancellation policy may apply.'
    );
    if (!confirmed) {
      return;
    }

    setSubmittingBookingId(bookingId);
    setError(null);
    try {
      await apiFetch(`/api/bookings/${bookingId}/cancel`, {
        method: 'POST',
        session,
      });
      await loadOverview();
    } catch (cancelError) {
      const message = cancelError instanceof Error ? cancelError.message : 'Cancellation failed';
      setError(message);
    } finally {
      setSubmittingBookingId(null);
    }
  };

  const handleReschedule = async (bookingId: string) => {
    if (!rescheduleSlotId) {
      setError('Choose a new slot to reschedule');
      return;
    }
    setSubmittingBookingId(bookingId);
    setError(null);
    try {
      await apiFetch(`/api/bookings/${bookingId}/reschedule`, {
        method: 'POST',
        session,
        body: JSON.stringify({ newSlotId: rescheduleSlotId }),
      });
      setRescheduleBookingId(null);
      setRescheduleSlotId('');
      await loadOverview();
    } catch (rescheduleError) {
      const message =
        rescheduleError instanceof Error ? rescheduleError.message : 'Reschedule failed';
      setError(message);
    } finally {
      setSubmittingBookingId(null);
    }
  };

  const adminAccess = useMemo(() => data?.profile?.role === 'admin', [data?.profile?.role]);
  const selectedRescheduleOptions = useMemo(() => {
    if (!rescheduleBookingId) {
      return [];
    }
    const booking = data?.upcomingBookings.find((entry) => entry.id === rescheduleBookingId);
    if (!booking) {
      return [];
    }

    return openSlots
      .filter(
        (slot) =>
          slot.lesson_type_id === booking.lesson_type_id &&
          slot.starts_at !== booking.starts_at &&
          slot.id !== booking.slot_id
      )
      .slice(0, 60);
  }, [data?.upcomingBookings, openSlots, rescheduleBookingId]);

  const handleBuyPackage = async (packageProductId: string) => {
    setError(null);
    try {
      const response = await apiFetch<{ checkoutUrl: string }>('/api/checkout/package', {
        method: 'POST',
        session,
        body: JSON.stringify({
          packageProductId,
        }),
      });
      window.location.assign(response.checkoutUrl);
    } catch (purchaseError) {
      const message =
        purchaseError instanceof Error ? purchaseError.message : 'Failed to initialize checkout';
      setError(message);
    }
  };

  if (canShowGate) {
    return (
      <main className="min-h-screen bg-slate-50 px-4 py-16">
        <div className="mx-auto max-w-xl rounded-2xl border border-border bg-white p-6">
          <h1 className="font-heading text-3xl font-bold text-primary">Your account</h1>
          <p className="mt-2 text-sm text-foreground/70">
            Sign in to manage bookings and package credits.
          </p>
          <div className="mt-4 flex gap-3">
            <Link
              href="/auth/sign-in?next=/account"
              className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white"
            >
              Sign in
            </Link>
            <Link
              href="/book"
              className="rounded-lg border border-border px-4 py-2 text-sm font-semibold"
            >
              Back to booking
            </Link>
          </div>
        </div>
      </main>
    );
  }

  if (!authLoading && user && profile?.role === 'admin') {
    return (
      <main className="min-h-screen bg-slate-50 px-4 py-16">
        <div className="mx-auto max-w-xl rounded-2xl border border-border bg-white p-6">
          <h1 className="font-heading text-3xl font-bold text-primary">Admin session</h1>
          <p className="mt-2 text-sm text-foreground/70">
            Admin accounts use the admin dashboard only. Redirecting now.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-10 md:px-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <section className="rounded-2xl border border-border bg-white p-6">
          <h1 className="font-heading text-3xl font-bold text-primary">Account dashboard</h1>
          <p className="mt-2 text-sm text-foreground/70">
            Manage bookings, credits, and cancellation/reschedule actions.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link
              href="/book"
              className="rounded-lg border border-border px-4 py-2 text-sm font-medium"
            >
              Book a lesson
            </Link>
            {adminAccess && (
              <Link
                href="/admin"
                className="rounded-lg border border-border px-4 py-2 text-sm font-medium"
              >
                Admin session
              </Link>
            )}
            <button
              type="button"
              onClick={() => signOut()}
              className="rounded-lg border border-border px-4 py-2 text-sm font-medium"
            >
              Sign out
            </button>
          </div>
        </section>

        {error && (
          <p aria-live="assertive" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </p>
        )}

        <section className="rounded-2xl border border-border bg-white p-6">
          <h2 className="font-heading text-xl font-semibold text-primary">Credit balances</h2>
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            {loading ? (
              <p className="text-sm text-foreground/70">Loading balances...</p>
            ) : data?.balances?.length ? (
              data.balances.map((balance) => (
                <div key={balance.id} className="rounded-lg border border-border px-4 py-3">
                  <p className="text-sm font-semibold">
                    {balance.lesson_types?.name} ({balance.lesson_types?.duration_minutes} min)
                  </p>
                  <p className="text-2xl font-bold text-primary">{balance.balance}</p>
                </div>
              ))
            ) : (
              <p className="text-sm text-foreground/70">
                No credits yet. Admin can allocate them in admin session.
              </p>
            )}
          </div>
        </section>

        <section className="rounded-2xl border border-border bg-white p-6">
          <h2 className="font-heading text-xl font-semibold text-primary">Buy packages</h2>
          <p className="mt-2 text-sm text-foreground/70">
            Buy a package and credits will be added to your account after successful payment.
          </p>
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            {loading ? (
              <p className="text-sm text-foreground/70">Loading packages...</p>
            ) : packages.length === 0 ? (
              <p className="text-sm text-foreground/70">
                No packages are currently available for purchase.
              </p>
            ) : (
              packages.map((entry) => (
                <div key={entry.id} className="rounded-lg border border-border p-3">
                  <p className="text-sm font-semibold">{entry.name}</p>
                  <p className="text-xs text-foreground/70">
                    {entry.duration_minutes} min | {entry.credits_granted} credits
                  </p>
                  <p className="mt-1 text-lg font-bold text-primary">
                    EUR {(entry.price_cents / 100).toFixed(2)}
                  </p>
                  {entry.package_size === 10 && (
                    <p className="text-xs text-foreground/60">
                      Includes personal development plan and video analysis.
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={() => handleBuyPackage(entry.id)}
                    className="mt-2 w-full rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-white"
                  >
                    Buy now
                  </button>
                </div>
              ))
            )}
          </div>
        </section>

        <section className="rounded-2xl border border-border bg-white p-6">
          <h2 className="font-heading text-xl font-semibold text-primary">Upcoming bookings</h2>
          <div className="mt-4 space-y-3">
            {!loading && !data?.upcomingBookings?.length && (
              <p className="text-sm text-foreground/70">No upcoming bookings.</p>
            )}
            {data?.upcomingBookings?.map((booking) => (
              <div key={booking.id} className="rounded-lg border border-border p-4">
                <p className="font-semibold text-foreground">
                  {booking.lesson_types?.name} at {booking.locations?.name}
                </p>
                <p className="text-sm text-foreground/70">{formatLessonDate(booking.starts_at)}</p>
                <p className="mt-1 text-xs text-foreground/60">
                  {getCancellationPolicySummary(booking.starts_at)}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    disabled={submittingBookingId === booking.id}
                    onClick={() => handleCancel(booking.id)}
                    className="rounded-md border border-border px-3 py-1.5 text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    disabled={submittingBookingId === booking.id}
                    onClick={() => {
                      setRescheduleBookingId((previous) =>
                        previous === booking.id ? null : booking.id
                      );
                      setRescheduleSlotId('');
                    }}
                    className="rounded-md border border-border px-3 py-1.5 text-sm"
                  >
                    Reschedule
                  </button>
                </div>
                {rescheduleBookingId === booking.id && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <select
                      aria-label="Choose a new slot"
                      value={rescheduleSlotId}
                      onChange={(event) => setRescheduleSlotId(event.target.value)}
                      className="min-w-[240px] rounded-md border border-border px-3 py-1.5 text-sm"
                    >
                      <option value="">Choose an available slot</option>
                      {selectedRescheduleOptions.map((slot) => (
                        <option key={slot.id} value={slot.id}>
                          {formatLessonDate(slot.starts_at)}
                        </option>
                      ))}
                    </select>
                    <button
                      disabled={submittingBookingId === booking.id}
                      onClick={() => handleReschedule(booking.id)}
                      className="rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-white"
                    >
                      Confirm reschedule
                    </button>
                    {selectedRescheduleOptions.length === 0 && (
                      <p className="w-full text-xs text-foreground/60">
                        No alternative slots are currently available for this lesson type.
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-2xl border border-border bg-white p-6">
          <h2 className="font-heading text-xl font-semibold text-primary">Past bookings</h2>
          <div className="mt-4 space-y-2">
            {!loading && !data?.pastBookings?.length && (
              <p className="text-sm text-foreground/70">No past bookings yet.</p>
            )}
            {data?.pastBookings?.map((booking) => (
              <div key={booking.id} className="rounded-lg border border-border px-4 py-3">
                <p className="font-medium text-foreground">
                  {booking.lesson_types?.name} at {booking.locations?.name}
                </p>
                <p className="text-sm text-foreground/70">{formatLessonDate(booking.starts_at)}</p>
                <p className="text-xs text-foreground/60">
                  Status: {booking.status} | Payment: {booking.payment_status}
                </p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </main>
  );
}
